import { useState } from 'react';
import { PiWarehouse, PiCheckCircle } from 'react-icons/pi';
import { Button } from '@v2/design-system';
import { cn } from '@v2/utils/cn';
import { useI18n } from '@v2/providers/I18nProvider';
import {
    StockPhaseBadge,
    StockShipmentLink,
    phaseFromSource,
} from '@v2/features/products/StockPhaseBadge';
import type { OrderProductCard } from '../types';
import StockRequestModal from './StockRequestModal';

type Props = {
    dealId: number;
    product: OrderProductCard;
    canRequestStock: boolean;
};

function isActiveReservation(status?: string | null): boolean {
    return status === 'reserved' || status === 'pending' || status === 'approved';
}

export default function DealFulfillmentLineControls({ dealId, product, canRequestStock }: Props) {
    const { t } = useI18n();
    const [open, setOpen] = useState(false);
    const request = product.stock_request;
    const stock = product.stock_available ?? 0;
    const available = product.available_to_request ?? stock;
    const inProcess = product.shipment_available_total ?? 0;
    const active = isActiveReservation(request?.status);
    const showAction = canRequestStock || Boolean(request);
    const phase = request?.stock_phase ?? phaseFromSource(request?.source);
    const phaseLabel = request?.source_label
        ?? (phase === 'transito' ? t('v2.stock_phase.transito') : t('v2.stock_phase.terra'));

    let statusLabel = 'Importar';
    let statusClass =
        'bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300';
    let StatusIcon: typeof PiWarehouse | null = null;

    if (active) {
        const qty = request?.qty_approved ?? request?.qty_requested ?? 0;
        statusLabel = `Reservado · ${qty} un.`;
        statusClass = 'bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-200';
        StatusIcon = PiCheckCircle;
    }

    const actionLabel = active ? 'Ver reserva' : 'Reservar estoque';

    return (
        <>
            <div className="mt-auto w-full border-t border-gray-100 pt-2.5 dark:border-zinc-800">
                <div className="flex flex-col items-stretch gap-1.5">
                    <div
                        className={cn(
                            'inline-flex w-full items-center justify-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium',
                            statusClass,
                        )}
                    >
                        {StatusIcon && <StatusIcon className="size-3 shrink-0" />}
                        <span className="truncate">{statusLabel}</span>
                    </div>

                    {active ? (
                        <div className="flex flex-wrap items-center justify-center gap-1.5">
                            <StockPhaseBadge phase={phase} label={phaseLabel} />
                            {request?.source === 'shipment' && (
                                <StockShipmentLink
                                    shipmentId={request.source_shipment?.id ?? request.source_shipment_id}
                                    label={request.source_shipment?.label}
                                />
                            )}
                        </div>
                    ) : (
                        <p className="text-center text-[10px] leading-tight text-gray-400 dark:text-zinc-500">
                            {available > 0 || inProcess > 0
                                ? [
                                      available > 0
                                          ? `${available} ${t('v2.stock_phase.terra').toLowerCase()}`
                                          : null,
                                      inProcess > 0
                                          ? `${inProcess} ${t('v2.stock_phase.transito').toLowerCase()}`
                                          : null,
                                  ]
                                      .filter(Boolean)
                                      .join(' · ')
                                : `Sem ${t('v2.stock_phase.terra').toLowerCase()} nem ${t('v2.stock_phase.transito').toLowerCase()}`}
                        </p>
                    )}

                    {showAction && (
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-8 w-full justify-center gap-1.5 text-[11px] font-medium"
                            onClick={() => setOpen(true)}
                            disabled={!canRequestStock && !request}
                        >
                            <PiWarehouse className="size-3.5 shrink-0" />
                            {actionLabel}
                        </Button>
                    )}
                </div>
            </div>
            <StockRequestModal
                open={open}
                onClose={() => setOpen(false)}
                dealId={dealId}
                product={product}
                canRequest={canRequestStock}
            />
        </>
    );
}
